import { useEffect } from "react";
import { ArrowRight, Quote } from "lucide-react";
import { Reveal, ASSETS } from "@/lib/motion";
import Nav from "@/components/bwg/Nav";
import Footer from "@/components/bwg/Footer";
import WhatsAppFab from "@/components/bwg/WhatsAppFab";
import InteriorHero from "@/components/bwg/InteriorHero";
import Seo from "@/components/bwg/Seo";
import { useBooking } from "@/components/bwg/BookingModal";
import { orgSchema, breadcrumbSchema } from "@/lib/schema";

const STATS = [
  { value: "₹6.84 Cr → ₹17.72 Cr", label: "Combined, closed exits", sub: "2 realized exits shown below" },
  { value: "5.5 mo", label: "Average holding period" },
  { value: "8.75", label: "FAR, both parcels" },
  { value: "1", label: "Corridor: Ram Mandir Connecting Corridor Rd" },
];

const CASES = [
  {
    id: "prem-prakash",
    tag: "Case Study 01 · Prem Prakash",
    photo: "/assets/prem-prakash.jpg",
    location: "Ram Mandir Connecting Corridor Road, Ayodhya, UP",
    multiple: "2.5×",
    multipleNote: "realized, 1st parcel · 2nd exit ongoing",
    chips: ["FAR 8.75", "Commercial CLU", "37,000 sqft · Parcel 1", "10,200 sqft · Parcel 2", "Repeat investor"],
    timeline: [
      { date: "16 Dec 2025", event: "Parcel 1 acquired", amount: "₹1.42 Cr" },
      { date: "22 May 2026", event: "Parcel 1 exited, post commercial CLU, 5.5-month hold", amount: "₹3.55 Cr" },
      { date: "30 Jun 2026", event: "Parcel 2 acquired, same corridor", amount: "₹1.87 Cr" },
      { date: "Ongoing", event: "Parcel 2 exit in progress", amount: "₹6.00 Cr" },
    ],
    steps: [
      { name: "Demand & Supply Intelligence", body: "Tracked the ₹300 Cr road development tender announced 30 Apr 2023 and flagged the corridor early as a coming commercial stretch." },
      { name: "Master Plan & Regulatory", body: "Cross-checked the parcel against Master Plan 2031 and confirmed CLU applicability at an FAR of 8.75." },
      { name: "Commercial Research", body: "Mapped tourist footfall and traffic off the Varanasi highway, and found a real shortage of boutique-hotel and commercial land inventory on this stretch." },
      { name: "Asset Development", body: "Completed post-registry documentation and secured mandates from hospitality and retail occupiers." },
      { name: "Asset Exit", body: "Brought four commercial developers to the table at an average 2.4× of the initial investment, and structured the exit." },
    ],
    quote: "He came back for a second parcel on the same road before the first one had even closed.",
    quoteBy: "A repeat investor, on the same corridor twice.",
    footnote: "Parcel 1's numbers are closed and final; Parcel 2's exit is still in progress at the figure shown.",
  },
  {
    id: "kamal-kukreja",
    tag: "Case Study 02 · Dr. Kamal Kukreja",
    photo: "/assets/kamal-kukreja.jpg",
    location: "Ram Mandir Connecting Corridor Road, Ayodhya, UP",
    multiple: "2.61×",
    multipleNote: "realized in 5.5 months",
    chips: ["FAR 8.75", "Commercial CLU", "37,000 sqft", "Road-facing parcel", "Institutional exit"],
    timeline: [
      { date: "16 Dec 2025", event: "Parcel acquired, road-facing", amount: "₹5.42 Cr" },
      { date: "22 May 2026", event: "Exited to an institutional hospitality buyer, post commercial CLU, 5.5-month hold", amount: "₹14.17 Cr" },
    ],
    steps: [
      { name: "Demand & Supply Intelligence", body: "Identified the mega corridor ahead of the market by tracking the proposed 45-metre road tender, and acquired a road-facing parcel on it." },
      { name: "Master Plan & Regulatory", body: "Mapped the parcel against Master Plan 2031 ahead of its release, confirming CLU applicability and an FAR of 8.75." },
      { name: "Commercial Research", body: "Studied the coming demand ecosystem: 7 mega township projects and 4 upcoming 100-key hotels on the identified corridor." },
      { name: "Asset Development", body: "Completed title conversion to commercial CLU and secured Naksha approval for a mixed-use retail and hotel development across the full permissible FAR." },
      { name: "Asset Exit", body: "Positioned the asset for institutional hospitality buyers, exited to a Bangalore-based group planning a 90-key hotel and retail development." },
    ],
    quote: "The building plan alone, a 90-key hotel on a parcel we bought as agricultural-adjacent land, is the clearest single proof of what FAR 8.75 actually means.",
    quoteBy: "On the buyer's Naksha approval.",
    footnote: "A single, closed transaction, record available on request.",
  },
];

const TESTIMONIALS = [
  { name: "Prem Prakash Ji", place: "Delhi", photo: "/assets/prem-prakash.jpg", quote: "Thanks to BWG for a smooth and successful transaction. Their understanding of land is second to none." },
  { name: "Mr. Kamal Kukreja", place: "", photo: "/assets/kamal-kukreja.jpg", quote: "BWG has helped me with a successful transaction. What they say they do, they do with class and excellence." },
  { name: "Dr. Vijay Kohli", place: "Delhi", quote: "What stood out most was BWG's unmatched local insight and disciplined approach. The transaction couldn't have gone more smoothly." },
];

function CaseStudy({ c, index }) {
  return (
    <section className={`py-24 lg:py-32 ${index % 2 === 0 ? "bg-brand-offwhite" : "bg-brand-paper"}`} data-testid={`case-study-${c.id}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-4">
                {c.photo && (
                  <img src={c.photo} alt={c.tag.split("· ")[1]} data-testid={`case-photo-${c.id}`}
                    className="w-[72px] h-[72px] lg:w-20 lg:h-20 rounded-full object-cover border-2 border-brand-orange/40 shrink-0" />
                )}
                <div>
                  <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">{c.tag}</span>
                  <p className="mt-3 text-sm text-brand-grey flex items-center gap-2">{c.location}</p>
                </div>
              </div>
              <h2 className="mt-4 font-display font-semibold text-6xl sm:text-7xl tracking-tighter text-brand-ink leading-[1.0]">{c.multiple}</h2>
              <p className="mt-2 text-lg text-brand-grey">{c.multipleNote}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-6 flex flex-wrap gap-2">
                {c.chips.map((chip) => (
                  <span key={chip} className="text-xs font-medium tracking-wide text-brand-ink bg-brand-card border border-brand-ink/10 px-3 py-1.5 rounded-sm">{chip}</span>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="border border-brand-ink/10 rounded-sm overflow-hidden">
                {c.timeline.map((t, i) => (
                  <div key={i} className={`grid grid-cols-[110px_1fr_auto] items-baseline gap-4 px-5 py-4 ${i > 0 ? "border-t border-brand-ink/10" : ""} ${t.date === "Ongoing" ? "bg-brand-card" : "bg-brand-offwhite"}`}>
                    <span className="font-mono text-xs text-brand-grey">{t.date}</span>
                    <span className="text-sm text-brand-ink leading-snug">{t.event}</span>
                    <span className="font-display font-semibold text-sm text-brand-ink whitespace-nowrap">{t.amount}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-brand-ink/10 border border-brand-ink/10">
          {c.steps.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.08} className={`${index % 2 === 0 ? "bg-brand-offwhite" : "bg-brand-paper"} p-6`}>
              <span className="font-mono text-sm text-brand-ink/20">0{i + 1}</span>
              <h3 className="mt-3 font-display font-semibold text-base tracking-tight text-brand-ink leading-snug">{s.name}</h3>
              <p className="mt-2 text-brand-grey leading-relaxed text-sm">{s.body}</p>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <figure className="mt-14 max-w-3xl">
            <Quote className="w-8 h-8 text-brand-orange mb-4" strokeWidth={1.5} />
            <blockquote className="font-display text-2xl sm:text-3xl tracking-tight text-brand-ink leading-snug">{c.quote}</blockquote>
            <figcaption className="mt-4 text-sm text-brand-grey">{c.quoteBy}</figcaption>
          </figure>
          <p className="mt-8 text-xs text-brand-grey/70">{c.footnote}</p>
        </Reveal>
      </div>
    </section>
  );
}

export default function SuccessStories() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { open: openBooking } = useBooking();
  return (
    <main className="bg-brand-paper overflow-x-hidden">
      <Seo
        title="Success Stories | Bhaarat Wealth Group"
        description="Two realized exits on Ayodhya's Ram Mandir Connecting Corridor Road, with dates, amounts and outcomes as they occurred, each checkable at the sub-registrar's office."
        path="/success-stories"
        schema={[orgSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Success Stories", path: "/success-stories" }])]}
      />
      <Nav overHero />
      <InteriorHero img={ASSETS.ayodhyaInfra} eyebrow="Success Stories · A Glimpse of 24 Closed Transactions"
        title="Two exits. Nothing projected."
        sub="These two are a sample from one corridor, not the full picture. Across every corridor, BWG has closed 24 transactions and deployed ₹92 Cr to date. What follows is exactly what happened on this one: dates, amounts, and outcomes as they occurred, each checkable at the sub-registrar's office. You hold title in your own name throughout." />

      {/* Stat strip */}
      <section className="bg-brand-dark py-16 border-b border-brand-offwhite/10" data-testid="success-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <div key={i} className="text-center">
              <div className="font-display font-semibold text-2xl sm:text-3xl tracking-tighter text-brand-offwhite">{s.value}</div>
              <p className="mt-2 text-sm text-brand-offwhite/55">{s.label}</p>
              {s.sub && <p className="mt-1 text-xs text-brand-offwhite/40">{s.sub}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* The investors lede */}
      <section className="bg-brand-offwhite pt-24 lg:pt-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">The investors</span>
            <p className="mt-4 text-lg text-brand-grey leading-relaxed max-w-3xl">Two of the firm's 24 closed transactions: full case files, exactly as sourced, diligenced, structured and exited by each of BWG's five internal teams.</p>
          </Reveal>
        </div>
      </section>

      {CASES.map((c, i) => <CaseStudy key={c.id} c={c} index={i} />)}

      {/* In their words */}
      <section className="bg-brand-offwhite py-24 lg:py-32 border-t border-brand-ink/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">In their words</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-ink leading-[1.05] max-w-2xl">What investors say after the exit.</h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <div data-testid={`testimonial-card-${i}`} className="h-full bg-brand-card border border-brand-ink/10 rounded-sm p-8 flex flex-col">
                  <Quote className="w-7 h-7 text-brand-orange mb-5" strokeWidth={1.5} />
                  <p className="text-brand-ink leading-relaxed flex-1">"{t.quote}"</p>
                  <div className="mt-6 pt-5 border-t border-brand-ink/10 flex items-center gap-3">
                    {t.photo && (
                      <img src={t.photo} alt={t.name} data-testid={`testimonial-photo-${i}`}
                        className="w-10 h-10 rounded-full object-cover border border-brand-ink/10" />
                    )}
                    <div>
                      <p className="font-display font-semibold text-brand-ink">{t.name}</p>
                      {t.place && <p className="text-sm text-brand-grey">{t.place}</p>}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-brand-dark py-24 lg:py-32 relative overflow-hidden">
        <div className="grain" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <Reveal>
            <h3 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-brand-offwhite leading-[1.05]">Talk to our investors, before you talk to us.</h3>
            <p className="mt-6 text-lg text-brand-offwhite/70 leading-relaxed max-w-2xl mx-auto">Ask Prem, or the next investor on this list, anything: the diligence, the paperwork, the exit. Then decide.</p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <button type="button" onClick={() => openBooking("default", "success_stories_cta")} data-testid="success-reference-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-4 font-semibold text-white hover:bg-brand-offwhite hover:text-brand-ink transition-colors duration-300">
                Request a reference call <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>
              <button type="button" onClick={() => openBooking("default", "success_stories_cta")} data-testid="success-book-cta"
                className="group inline-flex items-center gap-2 rounded-full border border-brand-offwhite/30 px-8 py-4 font-semibold text-brand-offwhite hover:border-brand-orange hover:text-brand-orange transition-colors duration-300">
                Book a 15-minute call <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
      <WhatsAppFab />
    </main>
  );
}
